import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheck, FaCheckCircle, FaSyringe, FaUserMd, FaUtensils,
  FaDumbbell, FaFlask, FaMobileAlt, FaShieldAlt, FaStar,
  FaArrowRight, FaWhatsapp, FaCalendarAlt,
  FaHeartbeat, FaClipboardCheck, FaVideo, FaBox,
  FaTimes, FaPlus, FaMinus, FaChartLine, FaAward,
  FaPhone, FaLeaf, FaUsers
} from 'react-icons/fa';

// ─── Data ────────────────────────────────────────────────────────────────────

const faqs = [
  {
    q: 'How much weight will I lose?',
    a: 'Clinical trials show an average 15–20% body weight loss in 6–12 months. Individual results vary based on starting weight, adherence, and metabolism. Our doctors give realistic expectations during your consultation — no false promises.'
  },
  {
    q: 'Is GLP-1 safe for Indians?',
    a: 'Yes. GLP-1 medications are FDA-approved and used globally, including in India. Studies show similar efficacy and safety in South Asian populations. Medical screening and ongoing monitoring are required, which is exactly what our program provides.'
  },
  {
    q: 'What are the common side effects?',
    a: 'The most common are nausea (usually temporary), diarrhea, and constipation. These are managed through a slow dose increase and dietary adjustments. Serious side effects are rare but require ongoing doctor monitoring — which is built into our program.'
  },
  {
    q: 'Do I inject myself?',
    a: 'Yes, but it is much easier than it sounds. We provide an injection training video and support. The pen is pre-filled with a tiny needle. Most patients are comfortable after the first week. Depending on your medication, it is weekly or daily.'
  },
  {
    q: 'Can I eat Indian food?',
    a: "Absolutely — that's our specialty! We create personalised meal plans with roti, dal, rice, sabzi. You can eat at family functions and festivals. We teach smart portion control and healthy Indian cooking methods, not quinoa salads."
  },
  {
    q: 'What happens when I stop medication?',
    a: 'Some weight regain is possible if lifestyle habits are not maintained. Our program focuses on building sustainable habits so results last. Some patients taper off; others stay on a maintenance dose. Every exit is doctor-guided.'
  },
  {
    q: 'How is this different from HealthifyMe or Fitelo?',
    a: 'We provide actual prescription medication (not just coaching), doctor supervision with bi-weekly consultations, monthly lab monitoring, and disease management for PCOD, diabetes, and thyroid issues. Apps cannot prescribe or monitor your health.'
  },
  {
    q: 'Will insurance cover this?',
    a: 'Most Indian insurance does not cover weight-loss medication yet. Some corporate policies might. We provide detailed GST invoices for reimbursement attempts. EMI options via Bajaj Finserv and ZestMoney are also available.'
  },
  {
    q: 'What if it does not work for me?',
    a: 'After 3 months, if you have not lost at least 5% body weight despite full adherence, your doctor will reassess — adjusting medication, dosage, or approach. This is very rare with a properly supervised program.'
  },
  {
    q: 'How do I get authentic medication?',
    a: 'We partner with licensed pharmacies and authorised distributors only. Medication is delivered to your home in temperature-controlled packaging with tamper-proof seals. You will never source medication yourself.'
  }
];

const testimonials = [
  {
    name: 'Priya S.',
    age: 34,
    city: 'Mumbai',
    before: '82 kg',
    after: '65 kg',
    loss: '−17 kg',
    duration: '6 months',
    conditions: ['PCOD', 'Pre-diabetes'],
    hba1c: '6.1 → 5.4',
    quote: "After my PCOD diagnosis I tried everything — diets, gyms, apps. Nothing worked. Fitnession's doctors understood my hormones, customised my treatment, and I lost 17 kg without giving up roti and dal. My HbA1c normalised too!",
    initials: 'PS',
    color: 'from-purple-500 to-pink-500'
  },
  {
    name: 'Arjun M.',
    age: 42,
    city: 'Bangalore',
    before: '105 kg',
    after: '83 kg',
    loss: '−22 kg',
    duration: '7 months',
    conditions: ['Type 2 Diabetes', 'Hypertension'],
    hba1c: '8.2 → 6.8',
    quote: 'Sitting at a desk 10 hours a day had pushed me to 105 kg and uncontrolled diabetes. The combination of medication and weekly coaching changed my eating habits permanently. My doctor reduced my diabetes medication after month 4.',
    initials: 'AM',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    name: 'Neha R.',
    age: 29,
    city: 'Delhi',
    before: '91 kg',
    after: '74 kg',
    loss: '−17 kg',
    duration: '6 months',
    conditions: ['PCOD', 'Hypothyroid'],
    hba1c: 'N/A',
    quote: "I was told losing weight with thyroid issues was nearly impossible. Fitnession's endocrinologist optimised my thyroid medication alongside GLP-1. Six months later I am 17 kg lighter and finally feel like myself again.",
    initials: 'NR',
    color: 'from-emerald-500 to-teal-500'
  }
];

const comparisonData = [
  { feature: 'GLP-1 Medication', alone: '✓ ₹18–22K/mo', apps: '✗', fitnession: '✓ Included' },
  { feature: 'Doctor Supervision', alone: '✗ Self-managed', apps: '✗', fitnession: '✓ Bi-weekly' },
  { feature: 'Indian Meal Plans', alone: '✗', apps: '⚡ Generic', fitnession: '✓ Customised' },
  { feature: 'Monthly Lab Work', alone: '✗ Pay extra', apps: '✗', fitnession: '✓ Included' },
  { feature: 'Side Effect Management', alone: '✗ On your own', apps: '✗', fitnession: '✓ Doctor support' },
  { feature: 'PCOD / Diabetes Care', alone: '✗', apps: '✗', fitnession: '✓ Specialist care' },
  { feature: 'WhatsApp Support 24/7', alone: '✗', apps: '⚡ Chatbot', fitnession: '✓ Real team' },
  { feature: 'Total Cost / Month', alone: '₹20–27K+', apps: '₹3–8K', fitnession: '₹24,999' },
];

const includedItems = [
  { icon: FaSyringe, title: 'GLP-1 Medication', color: 'text-purple-600 bg-purple-50', items: ['Ozempic or Saxenda (doctor decides)', 'Delivered to your doorstep', 'All doses included in price', 'Continuous supply monitoring'] },
  { icon: FaUserMd, title: 'Doctor Supervision', color: 'text-blue-600 bg-blue-50', items: ['Initial 60-min assessment', 'Bi-weekly video consultations', 'Prescription management', 'Emergency support 24/7'] },
  { icon: FaUtensils, title: 'Indian Nutrition Coaching', color: 'text-green-600 bg-green-50', items: ['Personalised meal plans (roti, dal, sabzi)', 'Weekly 1-on-1 nutritionist calls', 'Festival & wedding eating guides', 'Family meal adaptations'] },
  { icon: FaDumbbell, title: 'Lifestyle Support', color: 'text-orange-600 bg-orange-50', items: ['Customised fitness (no gym needed)', 'Habit coaching & accountability', 'Mental health support', 'Private community access'] },
  { icon: FaFlask, title: 'Lab Monitoring', color: 'text-red-600 bg-red-50', items: ['Monthly comprehensive blood work', 'HbA1c, lipids, liver & kidney', 'Progress tracking dashboard', 'Results explained by doctor'] },
  { icon: FaMobileAlt, title: 'Technology', color: 'text-indigo-600 bg-indigo-50', items: ['Mobile app for tracking', 'WhatsApp direct support', 'Video consultation platform', 'Progress photos & measurements'] },
];

const doctors = [
  { name: 'Dr. Priya Sharma', specialty: 'Endocrinology & Metabolic Health', experience: '15 years', hospitals: 'AIIMS, Fortis', tags: ['PCOD', 'Diabetes', 'Thyroid', 'Obesity'], quote: 'Every patient is unique. We customise treatment based on your body, lifestyle, and goals.', initials: 'PS' },
  { name: 'Dr. Rahul Mehta', specialty: 'Internal Medicine & Diabetes Care', experience: '12 years', hospitals: 'Max, Apollo', tags: ['Hypertension', 'Diabetes', 'Metabolic Syndrome'], quote: 'Sustainable weight loss is a medical journey. My job is to make it safe and effective for you.', initials: 'RM' },
  { name: 'Dr. Anjali Gupta', specialty: 'Obesity & Metabolic Medicine', experience: '10 years', hospitals: 'Medanta, Fortis', tags: ['Obesity', 'Pre-diabetes', 'PCOD', 'GLP-1'], quote: 'GLP-1 is not a shortcut — it is a medical tool. Combined with coaching, results are life-changing.', initials: 'AG' },
];

// ─── Component ───────────────────────────────────────────────────────────────

const GLP1Home = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="bg-white">

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left */}
            <div className="text-white">
              <div className="inline-flex items-center bg-gold-500/20 border border-gold-500/40 rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 rounded-full bg-gold-400 mr-2 animate-pulse" />
                <span className="text-gold-400 text-sm font-semibold tracking-wider uppercase">
                  Medical Weight Loss · India's First
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Medical Weight Loss
                <span className="block text-gold-400">That Actually Works</span>
                <span className="block">For Real Indians</span>
              </h1>

              <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-lg">
                Lose 15–20% body weight in 6 months with doctor-supervised GLP-1
                medication + personalised Indian lifestyle coaching
              </p>

              {/* Trust badges */}
              <div className="grid grid-cols-2 gap-2 mb-8">
                {['100% Doctor-Supervised', 'Authentic Medication', 'Indian Meal Plans', '50+ Patients Transformed'].map((b, i) => (
                  <div key={i} className="flex items-center text-sm text-gray-300">
                    <FaCheckCircle className="text-green-400 mr-2 flex-shrink-0" />
                    {b}
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link
                  to="/eligibility"
                  className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-xl hover:shadow-gold-500/40 hover:-translate-y-0.5"
                >
                  Check Your Eligibility
                  <FaArrowRight className="ml-2" />
                </Link>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center border-2 border-white/40 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 hover:border-white transition-all"
                >
                  View Pricing
                </a>
              </div>

              <p className="text-sm text-gray-400">
                ₹1,000 consultation fee, refunded if you enroll · No commitment required
              </p>
            </div>

            {/* Right — Stats Panel */}
            <div className="hidden lg:block">
              <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                {/* Main stat */}
                <div className="text-center mb-8">
                  <p className="text-6xl font-bold text-gold-400 leading-none">18%</p>
                  <p className="text-white text-xl font-semibold mt-2">Average Weight Loss</p>
                  <p className="text-gray-400 text-sm">in 6 months with our program*</p>
                </div>

                {/* 4 mini stats */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Patients Served', value: '50+', icon: FaUsers },
                    { label: 'Avg. Kg Lost', value: '14 kg', icon: FaChartLine },
                    { label: 'Doctor Rating', value: '4.8★', icon: FaStar },
                    { label: 'Program Success', value: '91%', icon: FaAward },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/10 rounded-xl p-4 text-center">
                      <s.icon className="text-gold-400 text-xl mx-auto mb-1" />
                      <p className="text-2xl font-bold text-white">{s.value}</p>
                      <p className="text-xs text-gray-400">{s.label}</p>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500 text-center mt-4">
                  *Based on clinical trials & patient data. Individual results vary.
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="w-full h-10 fill-white">
            <path d="M0,40 C360,0 1080,0 1440,40 L1440,40 L0,40 Z" />
          </svg>
        </div>
      </section>

      {/* ── STATS BAR ────────────────────────────────────────────────────── */}
      <section className="bg-white py-8 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '15–20%', label: 'Body Weight Lost', sub: 'in 6 months' },
              { value: '50+', label: 'Indians Transformed', sub: 'and counting' },
              { value: '3', label: 'Board-Certified Doctors', sub: 'on your team' },
              { value: '91%', label: 'Success Rate', sub: 'with full adherence' },
            ].map((s, i) => (
              <div key={i}>
                <p className="text-3xl font-bold text-navy-900">{s.value}</p>
                <p className="text-sm font-semibold text-gray-700 mt-0.5">{s.label}</p>
                <p className="text-xs text-gray-400">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLEM ──────────────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              You've Tried Everything. Nothing Worked.
            </h2>
            <p className="text-gray-500 text-lg">Sound familiar?</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                emoji: '🥗',
                label: 'DIET',
                title: 'Restrictive Diets Failed',
                text: "Gave up chai, roti, rice. Lost 3 kg, gained 5 kg back. Couldn't sustain quinoa salads and kale smoothies while your family ate normally."
              },
              {
                emoji: '🏋️',
                label: 'GYM',
                title: "Exercise Didn't Help",
                text: 'Worked out 5 days a week. Scale didn\'t budge. PCOD, thyroid, or insulin resistance made fat loss feel biologically impossible.'
              },
              {
                emoji: '📱',
                label: 'APP',
                title: 'Generic Apps Were Useless',
                text: 'HealthifyMe gave western meal plans. You needed help with Indian food at family weddings — not macros for a chicken salad.'
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 relative overflow-hidden group hover:shadow-lg transition-shadow">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 to-orange-400" />
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">{item.emoji}</span>
                  <span className="text-xs font-bold text-red-500 tracking-widest bg-red-50 px-2 py-0.5 rounded">{item.label}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-navy-900 text-white px-8 py-4 rounded-2xl">
              <p className="text-lg font-semibold">
                The problem isn't you.{' '}
                <span className="text-gold-400">Weight loss is MEDICAL,</span>{' '}
                not just motivational.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOLUTION ─────────────────────────────────────────────────────── */}
      <section id="solution" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">The Solution</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              GLP-1 Medication + Expert Coaching
            </h2>
            <p className="text-xl text-gray-500">India's Most Complete Medical Weight Loss Program</p>
          </div>

          <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-3xl p-8 lg:p-12 text-white">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">What is GLP-1?</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  GLP-1 medications work on your body's natural hunger hormones. They reduce appetite,
                  make you feel full faster and longer, and help regulate blood sugar — addressing the
                  <em> biological root cause</em> of weight gain.
                </p>
                <ul className="space-y-3">
                  {[
                    'Reduce appetite naturally at hormonal level',
                    'Feel full faster and longer',
                    'Regulate blood sugar and insulin',
                    'Support sustainable, lasting weight loss',
                    'FDA-approved, proven in 100,000+ patients'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start text-gray-200">
                      <FaCheckCircle className="text-gold-400 mr-3 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: '💊', title: 'Ozempic', sub: 'Semaglutide — weekly injection' },
                  { icon: '💉', title: 'Saxenda', sub: 'Liraglutide — daily injection' },
                  { icon: '🧬', title: 'Mounjaro', sub: 'Tirzepatide — dual action' },
                  { icon: '👨‍⚕️', title: 'Doctor Picks', sub: 'Right medication for you' },
                ].map((m, i) => (
                  <div key={i} className="bg-white/10 border border-white/20 rounded-xl p-4 text-center hover:bg-white/15 transition-colors">
                    <span className="text-3xl block mb-2">{m.icon}</span>
                    <p className="font-bold text-sm">{m.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{m.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">The Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Your 6-Month Transformation Journey
            </h2>
            <p className="text-gray-500">From first click to lasting results</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 relative">
            {/* Connecting line — desktop only */}
            <div className="absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-gold-200 hidden md:block" />

            {[
              { icon: FaClipboardCheck, step: '01', title: 'Eligibility Check', badge: 'Free · 5 min', color: 'bg-gold-500', items: ['Fill simple online form', 'Basic health info', 'Review within 24 hours'] },
              { icon: FaVideo, step: '02', title: 'Doctor Consultation', badge: '₹1,000 · 30 min', color: 'bg-blue-500', items: ['Video call with licensed doctor', 'Full medical history review', 'Get prescription if eligible'] },
              { icon: FaBox, step: '03', title: 'Onboarding', badge: 'Week 1', color: 'bg-green-500', items: ['Medication delivered home', 'Injection training video', 'Meet your nutrition coach'] },
              { icon: FaCalendarAlt, step: '04', title: 'Monthly Journey', badge: 'Months 1–6', color: 'bg-purple-500', items: ['Weekly coaching calls', 'Bi-weekly doctor check-ins', 'WhatsApp support 24/7'] },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md text-center relative z-10 hover:shadow-lg transition-shadow">
                <div className={`w-14 h-14 ${item.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <item.icon className="text-white text-xl" />
                </div>
                <div className="text-xs font-bold text-gray-400 mb-1">STEP {item.step}</div>
                <h3 className="text-base font-bold text-gray-900 mb-1">{item.title}</h3>
                <span className="inline-block text-xs bg-gold-100 text-gold-700 font-semibold px-2 py-0.5 rounded-full mb-3">{item.badge}</span>
                <ul className="space-y-1.5 text-left">
                  {item.items.map((t, j) => (
                    <li key={j} className="text-xs text-gray-600 flex items-start">
                      <FaCheck className="text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Progress timeline */}
          <div className="mt-10 bg-white rounded-2xl p-6 shadow-md">
            <p className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wider">Expected Progress</p>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { months: 'Month 1–2', kg: '3–5 kg', desc: 'Medication adjustment + side effect management', pct: 35 },
                { months: 'Month 3–4', kg: '8–12 kg', desc: 'New eating habits formed + accelerating results', pct: 70 },
                { months: 'Month 5–6', kg: '15–20%', desc: 'Target weight loss + metabolic improvements', pct: 100 },
              ].map((p, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold text-gray-700">{p.months}</span>
                    <span className="font-bold text-navy-900">{p.kg}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-1.5">
                    <div className="bg-gradient-to-r from-gold-400 to-gold-600 h-2 rounded-full transition-all" style={{ width: `${p.pct}%` }} />
                  </div>
                  <p className="text-xs text-gray-500">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ──────────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-green-600 font-semibold text-sm uppercase tracking-wider">All-Inclusive</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Everything You Need. One Program.
            </h2>
            <p className="text-gray-500 text-lg">Complete care at ₹24,999/month</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {includedItems.map((item, i) => (
              <div key={i} className="border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all group">
                <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="text-xl" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <ul className="space-y-2">
                  {item.items.map((text, j) => (
                    <li key={j} className="text-gray-600 flex items-start text-sm">
                      <FaCheck className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-500 mb-2">All of the above included for just:</p>
            <p className="text-4xl font-bold text-navy-900 mb-1">₹24,999<span className="text-xl font-normal text-gray-500">/month</span></p>
            <p className="text-sm text-gray-400 mb-6">Medication alone costs ₹18–22K elsewhere — without any of the above</p>
            <Link to="/eligibility" className="inline-flex items-center bg-gold-500 hover:bg-gold-600 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-gold-500/30">
              Check Your Eligibility <FaArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold-600 font-semibold text-sm uppercase tracking-wider">Pricing</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Transparent Pricing. No Hidden Costs.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Monthly Plan */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gold-500 overflow-hidden">
              <div className="bg-gradient-to-r from-navy-900 to-navy-800 text-white py-4 px-6 text-center">
                <FaStar className="inline text-gold-400 mr-2" />
                <span className="font-bold">GLP-1 + COMPLETE COACHING</span>
              </div>
              <div className="p-6">
                <div className="text-center mb-6">
                  <p className="text-5xl font-bold text-navy-900">₹24,999</p>
                  <p className="text-gray-500">/month · All-Inclusive</p>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {['GLP-1 Medication (Ozempic/Saxenda/Mounjaro)', 'Bi-weekly doctor consultations', 'Weekly nutrition coaching calls', 'Monthly comprehensive labs', '24/7 WhatsApp support', 'Mobile app access + community'].map((item, i) => (
                    <li key={i} className="flex items-center text-sm">
                      <FaCheckCircle className="text-green-500 mr-2.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/eligibility" className="block w-full text-center bg-gold-500 hover:bg-gold-600 text-white py-3.5 rounded-xl font-bold transition-colors">
                  Check Eligibility →
                </Link>
              </div>
            </div>

            {/* 6-Month Plan */}
            <div className="bg-navy-900 rounded-2xl shadow-xl overflow-hidden text-white relative">
              <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">BEST VALUE</div>
              <div className="p-6">
                <div className="mb-6">
                  <p className="text-gold-400 font-semibold text-sm mb-2">6-MONTH PREPAY</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-5xl font-bold">₹1,35,000</p>
                    <div>
                      <p className="text-gray-400 text-sm line-through">₹1,49,994</p>
                      <p className="text-green-400 text-sm font-bold">Save ₹15,000</p>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">= ₹22,500/month</p>
                </div>
                <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                  Best results happen at 6 months. Commit to the full journey, save ₹15,000, and get priority support.
                </p>
                <ul className="space-y-2.5 mb-6">
                  {['Everything in monthly plan', '₹15,000 savings vs monthly', 'Priority doctor scheduling', 'Extended medication stability'].map((item, i) => (
                    <li key={i} className="flex items-center text-sm">
                      <FaCheckCircle className="text-gold-400 mr-2.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/eligibility" className="block w-full text-center bg-gold-500 hover:bg-gold-600 text-white py-3.5 rounded-xl font-bold transition-colors">
                  Book Consultation →
                </Link>
              </div>
            </div>
          </div>

          {/* Cost breakdown + EMI */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-gray-900 mb-3">What It Would Cost Separately:</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  {[
                    ['Medication alone:', '₹18,000–22,000/month'],
                    ['Doctor visits:', '₹2,000–3,000/month'],
                    ['Nutritionist:', '₹5,000–8,000/month'],
                    ['Lab work:', '₹2,000/month'],
                  ].map(([l, v], i) => (
                    <div key={i} className="flex justify-between"><span>{l}</span><span>{v}</span></div>
                  ))}
                  <div className="flex justify-between border-t pt-2 font-bold text-gray-900">
                    <span>Total:</span><span>₹27,000–35,000/month</span>
                  </div>
                </div>
                <p className="text-green-600 font-bold mt-3 text-sm">
                  With Fitnession: ₹24,999/month — Save ₹2,000–10,000/month
                </p>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-3">Payment Options:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><FaCheck className="text-green-500 mr-2" />Monthly billing (cancel after 3 months)</li>
                  <li className="flex items-center"><FaCheck className="text-green-500 mr-2" />6-month prepay (best value — ₹22,500/mo)</li>
                  <li className="flex items-center"><FaCheck className="text-green-500 mr-2" />EMI via Bajaj Finserv & ZestMoney</li>
                  <li className="flex items-center"><FaCheck className="text-green-500 mr-2" />All major cards, UPI, net banking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Real People · Real Results</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              What Our Patients Say
            </h2>
            <p className="text-gray-500">Verified patients. Honest stories.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-shadow">
                {/* Header */}
                <div className={`bg-gradient-to-r ${t.color} p-6 text-white`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-white/30 rounded-full flex items-center justify-center text-xl font-bold">
                      {t.initials}
                    </div>
                    <div>
                      <p className="font-bold text-lg">{t.name}</p>
                      <p className="text-white/80 text-sm">{t.age} years · {t.city}</p>
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="flex gap-4">
                    <div className="bg-white/20 rounded-lg px-3 py-2 text-center flex-1">
                      <p className="text-xs text-white/70">Before</p>
                      <p className="font-bold">{t.before}</p>
                    </div>
                    <div className="bg-white/20 rounded-lg px-3 py-2 text-center flex-1">
                      <p className="text-xs text-white/70">After</p>
                      <p className="font-bold">{t.after}</p>
                    </div>
                    <div className="bg-white/30 rounded-lg px-3 py-2 text-center flex-1">
                      <p className="text-xs text-white/70">Lost</p>
                      <p className="font-bold">{t.loss}</p>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {t.conditions.map((c, j) => (
                      <span key={j} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                    {t.hba1c !== 'N/A' && (
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">HbA1c: {t.hba1c}</span>
                    )}
                  </div>
                  <blockquote className="text-gray-600 text-sm leading-relaxed italic mb-4">
                    "{t.quote}"
                  </blockquote>
                  <div className="flex items-center gap-1 text-gold-500">
                    {[...Array(5)].map((_, j) => <FaStar key={j} className="text-sm" />)}
                    <span className="text-xs text-gray-500 ml-2">Verified Patient · {t.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            *Results vary by individual. Average weight loss 15–20% in 6 months with full program adherence.
          </p>
        </div>
      </section>

      {/* ── DOCTORS ──────────────────────────────────────────────────────── */}
      <section id="doctors" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Medical Team</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Meet Your Doctors
            </h2>
            <p className="text-gray-500">Board-certified specialists. India-trained. Telemedicine-certified.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {doctors.map((doc, i) => (
              <div key={i} className="border border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-shadow group">
                {/* Avatar */}
                <div className="w-20 h-20 bg-gradient-to-br from-navy-800 to-navy-600 rounded-2xl flex items-center justify-center mb-4 mx-auto group-hover:scale-105 transition-transform">
                  <span className="text-2xl font-bold text-white">{doc.initials}</span>
                </div>
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{doc.name}</h3>
                  <p className="text-gold-600 font-medium text-sm">{doc.specialty}</p>
                  <div className="flex justify-center gap-3 mt-2 text-xs text-gray-500">
                    <span>📍 {doc.experience} exp.</span>
                    <span>🏥 {doc.hospitals}</span>
                  </div>
                </div>
                {/* Tags */}
                <div className="flex flex-wrap justify-center gap-1.5 mb-4">
                  {doc.tags.map((tag, j) => (
                    <span key={j} className="text-xs bg-navy-50 text-navy-700 px-2 py-0.5 rounded-full font-medium">#{tag}</span>
                  ))}
                </div>
                {/* Quote */}
                <blockquote className="text-xs text-gray-500 italic text-center border-t border-gray-100 pt-4">
                  "{doc.quote}"
                </blockquote>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-600">
            {['Medical Council of India Licensed', 'Telemedicine Certified', '1000+ Patients Treated', 'Continuous Medical Education'].map((b, i) => (
              <span key={i} className="flex items-center gap-1.5"><FaCheckCircle className="text-green-500" />{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO IS THIS FOR ──────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Is GLP-1 Right For You?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
                <FaCheckCircle className="text-green-600" /> You're a Good Candidate If:
              </h3>
              <ul className="space-y-2.5">
                {['BMI ≥ 30 (or ≥ 27 with health conditions)', 'Age 18–65 years', 'Struggled with lifestyle changes alone', 'Have PCOD, pre-diabetes, or metabolic issues', 'Can commit to 6+ months', 'Ready to invest ₹25K/month in your health', 'Want medical supervision', 'Ready to make sustainable changes'].map((item, i) => (
                  <li key={i} className="flex items-start text-green-800 text-sm">
                    <FaCheck className="mr-2 mt-0.5 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                <FaTimes className="text-red-600" /> Not Suitable If:
              </h3>
              <ul className="space-y-2.5">
                {['Pregnant or planning pregnancy soon', 'Currently breastfeeding', 'Personal/family history of thyroid cancer', 'Type 1 diabetes', 'Severe kidney or liver disease', 'Active eating disorder', 'Looking for quick fix without any effort', "Can't commit long-term"].map((item, i) => (
                  <li key={i} className="flex items-start text-red-800 text-sm">
                    <FaTimes className="mr-2 mt-0.5 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-500 mb-4">Not sure? Our doctors assess honestly — even if that means telling you GLP-1 isn't right for you.</p>
            <Link to="/eligibility" className="inline-flex items-center bg-gold-500 hover:bg-gold-600 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg">
              Check Your Eligibility Free <FaArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ─────────────────────────────────────────────── */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Fitnession vs. The Alternatives
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Feature</th>
                    <th className="p-4 text-center text-sm font-semibold text-gray-500">Medication Alone</th>
                    <th className="p-4 text-center text-sm font-semibold text-gray-500">Weight Loss Apps</th>
                    <th className="p-4 text-center text-sm font-bold text-navy-900 bg-gold-50">
                      <span className="flex items-center justify-center gap-1"><FaStar className="text-gold-500" /> Fitnession</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, i) => (
                    <tr key={i} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-gray-50/50' : ''}`}>
                      <td className="p-4 text-sm font-medium text-gray-700">{row.feature}</td>
                      <td className="p-4 text-center text-sm text-gray-500">
                        {row.alone.startsWith('✓') ? <span className="text-orange-600">{row.alone}</span>
                          : row.alone.startsWith('✗') ? <FaTimes className="text-red-400 mx-auto" />
                          : <span className="text-gray-500">{row.alone}</span>}
                      </td>
                      <td className="p-4 text-center text-sm text-gray-500">
                        {row.apps.startsWith('✓') ? <FaCheck className="text-green-500 mx-auto" />
                          : row.apps.startsWith('✗') ? <FaTimes className="text-red-400 mx-auto" />
                          : <span className="text-gray-500">{row.apps}</span>}
                      </td>
                      <td className="p-4 text-center text-sm font-semibold bg-gold-50">
                        {row.fitnession.startsWith('✓') ? (
                          <span className="text-green-700 flex items-center justify-center gap-1">
                            <FaCheckCircle className="text-green-500" />{row.fitnession.replace('✓ ', '')}
                          </span>
                        ) : <span className="text-navy-900">{row.fitnession}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-center text-gray-600 font-semibold mt-6">
            Get <span className="text-navy-900">MORE</span> for <span className="text-gold-600">LESS</span> with Fitnession's complete program
          </p>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Your Questions Answered
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  {openFaq === i
                    ? <FaMinus className="text-gold-500 flex-shrink-0" />
                    : <FaPlus className="text-gray-400 flex-shrink-0" />
                  }
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-gray-600 leading-relaxed text-sm border-t border-gray-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-gray-500 mb-4">Still have questions?</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://wa.me/919579520586" className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors">
                <FaWhatsapp className="mr-2" /> WhatsApp Us
              </a>
              <a href="tel:+919579520586" className="inline-flex items-center justify-center border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-xl font-semibold transition-colors">
                <FaPhone className="mr-2" /> Call Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── RESEARCH STRIP ───────────────────────────────────────────────── */}
      <section className="py-12 bg-navy-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FaFlask className="text-gold-400 text-3xl mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-3">Backed by Peer-Reviewed Science</h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            GLP-1 medications are not new or experimental. Backed by 10+ years of clinical trials involving 100,000+ patients across STEP, SCALE, SURMOUNT, and SUSTAIN trials.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm text-gray-300">
            {['STEP 1: 14.9% weight loss', 'SURMOUNT-1: 22.5%', 'SELECT: −20% CV events', 'Approved by FDA, EMA & CDSCO'].map((s, i) => (
              <span key={i} className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full">
                <FaLeaf className="text-gold-400 text-xs" />{s}
              </span>
            ))}
          </div>
          <Link to="/research" className="inline-flex items-center bg-gold-500 hover:bg-gold-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors">
            Read Full Research <FaArrowRight className="ml-2" />
          </Link>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-gold-500/20 border border-gold-500/40 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-gold-400 mr-2 animate-pulse" />
            <span className="text-gold-400 text-sm font-semibold">Limited Slots Available</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Transform
            <span className="block text-gold-400">Your Health?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join 50+ Indians who've lost 15–20% body weight with India's most comprehensive GLP-1 program
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link
              to="/eligibility"
              className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-600 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all shadow-xl hover:shadow-gold-500/40 hover:-translate-y-0.5"
            >
              Check Your Eligibility — Free
              <FaArrowRight className="ml-2" />
            </Link>
            <a
              href="https://wa.me/919579520586"
              className="inline-flex items-center justify-center border-2 border-white/40 text-white px-10 py-4 rounded-xl font-semibold hover:bg-white/10 hover:border-white transition-all"
            >
              <FaWhatsapp className="mr-2" />
              WhatsApp Us
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <span className="flex items-center gap-1.5"><FaShieldAlt className="text-gold-400" /> 100% Confidential</span>
            <span className="flex items-center gap-1.5"><FaUserMd className="text-gold-400" /> Licensed Doctors</span>
            <span className="flex items-center gap-1.5"><FaStar className="text-gold-400" /> 4.8/5 Rating</span>
            <span className="flex items-center gap-1.5"><FaHeartbeat className="text-gold-400" /> Medically Supervised</span>
          </div>

          <p className="text-xs text-gray-500 mt-8">
            ₹1,000 consultation fee · Refunded if you enroll · No commitment required
          </p>
        </div>
      </section>

    </div>
  );
};

export default GLP1Home;
